import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Tooltip } from '@sgme/ui';
import { format } from 'date-fns';
import type { ReactElement } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useRecordPriceMutation } from '@/api/mifid';
import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { getFlexiForwardRfsQuote } from '@/App/tiles/FlexiForward/utils.ts';
import { getFlexiForwardRfsId } from '@/store/flexi-forward/helpers/rfs.ts';
import { selectFlexiForwardCurrency } from '@/store/flexi-forward/selectors/properties.ts';
import { FULL_DATE_FORMAT_TEXT } from '@/utils/date.ts';

export function TimestampButton({ tileId }: { tileId: string }): ReactElement {
  const [recordPrice] = useRecordPriceMutation();

  const { mifid, rfsStatus, quoteId, rfsId, quoteCcy } = useFlexiForwardSelector((flexiForward) => {
    const quote = getFlexiForwardRfsQuote(flexiForward.properties.rfs);
    const currencyIndex =
      flexiForward.properties.upfrontCurrency === selectFlexiForwardCurrency(flexiForward).currency1 ? 1 : 2;

    return {
      rfsId: getFlexiForwardRfsId(flexiForward.properties.rfs),
      quoteId: quote?.quoteId,
      quoteCcy: currencyIndex === 1 ? quote?.premiumData.askAmountCcy1 : quote?.premiumData.askAmountCcy2,
      mifid: flexiForward.properties.mifid,
      rfsStatus: flexiForward.properties.rfs?.status,
    };
  });

  const hasBeenUsed = isDefined(mifid?.records?.length) ? mifid.records.length > 0 : false;
  const isDisabled = rfsStatus !== 'PRICING';

  function handleRecordPrice() {
    if (isNotDefined(rfsId) || isNotDefined(quoteCcy) || isNotDefined(quoteId)) {
      return;
    }

    recordPrice({
      tileId,
      date: Date.now(),
      quoteId,
      rfsId,
      prices: [
        {
          bid: 0,
          ask: Number.isNaN(Number(quoteCcy)) ? 0 : Number(quoteCcy),
        },
      ],
    });
  }

  return (
    <div className="d-flex gap-2">
      <Tooltip contents={<FormattedMessage id="option.footer.timestampBtn.tooltip" />}>
        <Button
          size="sm"
          purpose="primary"
          color={isDisabled || hasBeenUsed ? 'primary' : 'danger'}
          iconPosition="start"
          disabled={isDisabled}
          onClick={handleRecordPrice}
          data-e2e={'button-timestamp'}
        >
          <i className="icon icon-sm">timer</i>
          <FormattedMessage id="option.footer.timestampBtn" />
        </Button>
      </Tooltip>
      {!isDisabled && <PriceRecordHistory />}
    </div>
  );
}

function PriceRecordHistory() {
  const { currencyPair, mifid } = useFlexiForwardSelector((flexiForward) => ({
    currencyPair: selectFlexiForwardCurrency(flexiForward).currencyPair,
    mifid: flexiForward.properties.mifid || { records: [] },
  }));

  return (
    <OverlayTrigger
      rootClose
      placement="bottom"
      trigger="click"
      overlay={
        <Popover style={{ maxWidth: '300px' }}>
          <Popover.Header data-e2e="timestamp-popover-title">
            <div className="d-flex flex-column">
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.title"
                values={{ currencyPair, instrument: 'Option' }}
              />
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.subtitle"
                values={{
                  date: isDefined(mifid.rfsDate) ? format(mifid.rfsDate, FULL_DATE_FORMAT_TEXT) : '',
                }}
              />
            </div>
          </Popover.Header>

          <Popover.Body>
            <div className="d-flex flex-column gap-1" data-e2e="timestamp-popover-body">
              {mifid.records.toReversed().map((record, index) => (
                // oxlint-disable-next-line no-array-index-key -- index
                <div key={index}>
                  {record.isError ? (
                    <div className="text-danger">
                      <i className="icon icon-sm">error</i>
                      <FormattedMessage
                        id="priceRecordsHistory.popover.item.error"
                        values={{
                          item: mifid.records.length - index,
                          date: format(record.date, FULL_DATE_FORMAT_TEXT),
                        }}
                      />
                    </div>
                  ) : (
                    <FormattedMessage
                      id="priceRecordsHistory.popover.item"
                      values={{
                        item: mifid.records.length - index,
                        date: format(record.date, FULL_DATE_FORMAT_TEXT),
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      {mifid.records.some((r) => r.isError) ? (
        <div className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer text-danger">
          <i className="icon icon-sm">error</i>
          <FormattedMessage id="priceRecordsHistory.recording.error" />
        </div>
      ) : (
        <div
          data-e2e="timestamp-count"
          className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer"
        >
          {mifid.records?.length ?? 0}
        </div>
      )}
    </OverlayTrigger>
  );
}
